import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from "@tanstack/react-query";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DateTime } from "luxon";
import React from "react";
import { Link, Outlet, useSearchParams } from "react-router-dom";

import { useHasPermission } from "@/common/acl/guard/guard";
import { Failed, NoData } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import {
  itemsPerPage,
  Pagination,
} from "@/common/components/pagination/pagination";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { useToggle } from "@/common/hooks/use-toggle";
import { currencyFormatter, getPrice } from "@/common/services/formatter";
import {
  getPayments,
  GetPaymentsPaymentStatus,
  GetPaymentsPaymentType,
  PaymentToStatus,
} from "@/generated/api/users";
import { AutocompleteString } from "@/types";

import { Filter, SelectFilter } from "../components/table/filter/filter";
import { Table } from "../components/table/table";
import { Action } from "./components/action";
import { Invoice } from "./components/invoice";

const path = "payments";
const fallbackTable: ReturnType<typeof select>["data"] = [];

const Payments = () => {
  const { params, handleParamChange } = usePaymentsParams();
  const canEditPayment = useHasPermission("users:payment:confirm");
  const payments = useQuery({
    placeholderData: keepPreviousData,
    ...paymentsQuery(params),
  });
  const table = useReactTable({
    columns: [
      ...columns,
      ...(canEditPayment ? [invoiceColumn, actionColumn] : []),
    ],
    data: payments.data?.data ?? fallbackTable,
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
    manualPagination: true,
    state: {
      columnVisibility: {
        id: false,
      },
    },
  });
  if (payments.status === "pending" && payments.isLoading) {
    return (
      <>
        <Metadata title={t`Správa uživatelů`} />
        <Spinner className="mt-4" />
      </>
    );
  }

  if (payments.status === "error") {
    return <Failed error={payments.error} />;
  }

  return (
    <>
      <Metadata title={t`Správa plateb`} />
      <Table>
        <Table.Head
          filters={{
            status: (
              <SelectFilter
                column={table.getColumn("status")!}
                onFilterChange={handleParamChange}
                value={params.paymentStatus}
                values={Object.entries(PaymentToStatus).map(([key, value]) => ({
                  label: key,
                  value,
                }))}
              />
            ),
            type: (
              <SelectFilter
                column={table.getColumn("type")!}
                onFilterChange={handleParamChange}
                value={params.paymentType}
                values={[
                  { label: t`Platba kartou`, value: "CARD" },
                  { label: t`Bankovní převod`, value: "BANK_TRANSFER" },
                ]}
              />
            ),
            variableSymbol: (
              <Filter
                column={table.getColumn("variableSymbol")!}
                onFilterChange={handleParamChange}
                value={params.search}
              />
            ),
          }}
          table={table}
        />
        {payments.status === "success" && payments.data.data.length > 0 ? (
          <Table.Body table={table} />
        ) : null}
      </Table>
      {payments.data ? (
        <>
          <Spacer className="h-5" />
          <Pagination
            getter={() => parseInt(params.page, 10)}
            pageSize={itemsPerPage.oneHundred}
            setter={(page) => handleParamChange("page", page.toString())}
            total={payments.data.totalSize}
          />
        </>
      ) : null}
      {payments.status === "success" &&
      payments.fetchStatus === "idle" &&
      payments.data.data.length === 0 ? (
        <div className="mt-4">
          <NoData>
            <Trans>Nebyly nalezeny žádné záznamy</Trans>
          </NoData>
        </div>
      ) : null}
      {payments.fetchStatus === "fetching" && !payments.isLoading ? (
        <Spinner withHint={false} />
      ) : null}
      <Outlet />
    </>
  );
};

const columnHelper =
  createColumnHelper<ReturnType<typeof select>["data"][number]>();
const columns = [
  columnHelper.accessor("id", {}),
  columnHelper.accessor("createdAt", {
    cell: (info) =>
      DateTime.fromISO(info.getValue()).toLocaleString(DateTime.DATETIME_SHORT),
    header: () => t`Datum platby`,
    meta: {
      label: () => t`Datum platby`,
    },
  }),
  columnHelper.accessor(
    (row) => ({
      id: row.company.id,
      name: row.company.name,
    }),
    {
      cell: (info) => (
        <Link to={"company/" + info.getValue().id}>{info.getValue().name}</Link>
      ),
      header: () => t`Společnost`,
      id: "company",
      meta: {
        label: () => t`Společnost`,
      },
    },
  ),
  columnHelper.accessor(
    (row) => ({
      period: row.subscription.planPeriod,
      plan: row.subscription.planType,
    }),
    {
      cell: (info) => info.getValue().plan + " - " + info.getValue().period,
      header: () => t`Předmět platby`,
      id: "paymentSubject",
      meta: {
        label: () => t`Předmět platby`,
      },
    },
  ),
  columnHelper.accessor(
    (row) => ({
      gateway: row.gateway,
      gatewayId: row.gatewayId,
      variableSymbol: row.invoice.at(0)?.variable,
    }),
    {
      cell: (info) => (
        <VariableSymbolCell gatewayId={info.getValue().gatewayId}>
          {info.getValue().variableSymbol}
        </VariableSymbolCell>
      ),
      header: () => t`Variabilní symbol`,
      id: "variableSymbol",
      meta: {
        filterKey: "search",
        label: () => t`Variabilní symbol`,
      },
    },
  ),
  columnHelper.accessor("type", {
    cell: (info) =>
      info.getValue() === "CARD" ? t`Platba kartou` : t`Bankovní převod`,
    header: () => t`Typ`,
    meta: {
      filterKey: "paymentType",
      label: () => t`Typ`,
    },
  }),
  columnHelper.accessor("status", {
    cell: (info) => info.getValue(),
    header: () => t`Stav`,
    meta: {
      filterKey: "paymentStatus",
      label: () => t`Stav`,
    },
  }),
  columnHelper.accessor("amountTotal", {
    cell: (info) => info.getValue(),
    header: () => t`Částka`,
    meta: {
      label: () => t`Částka`,
    },
  }),
];

const VariableSymbolCell = ({
  gatewayId,
  children,
}: React.PropsWithChildren<{ gatewayId?: string }>) => {
  const toggle = useToggle();
  const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (e.ctrlKey) {
      toggle.toggle();
    }
  };

  return <span onClick={handleClick}>{toggle.on ? gatewayId : children}</span>;
};

const invoiceColumn = columnHelper.accessor("invoice", {
  cell: (info) => {
    return (
      <>
        {info.getValue().map((invoice, i, invoices) => (
          <React.Fragment key={invoice.id}>
            <Invoice id={invoice.id} key={invoice.id}>
              {invoice.type === "PAYMENT_REQUEST" ? (
                <Trans>Výzva k úhradě</Trans>
              ) : (
                <Trans>Faktura</Trans>
              )}
            </Invoice>
            {i === invoices.length - 1 ? "" : " | "}
          </React.Fragment>
        ))}
      </>
    );
  },
  header: () => t`Faktura`,
  meta: {
    label: () => t`Faktura`,
  },
});

const actionColumn = columnHelper.display({
  cell: ({ row }) =>
    row.getValue("type") === "BANK_TRANSFER" &&
    row.getValue("status") === "WAITING" ? (
      <Action paymentId={row.getValue("id")} />
    ) : null,
  header: () => "",
  id: "action",
});

const usePaymentsParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = {
    page: searchParams.get("page") ?? "1",
    paymentStatus: (searchParams.get("paymentStatus") ??
      "") as GetPaymentsPaymentStatus,
    paymentType: (searchParams.get("paymentType") ??
      "") as GetPaymentsPaymentType,
    search: searchParams.get("search") ?? "",
  };

  const handleParamChange = (
    key: AutocompleteString<"page" | "search" | "paymentType">,
    value: string,
  ) => {
    setSearchParams((c) => {
      c.set(key, value);
      return c;
    });
  };

  return { handleParamChange, params };
};

const select = (data: Awaited<ReturnType<typeof getPayments>>) => {
  return {
    ...data,
    data: data.data.map(({ currency, ...payment }) => ({
      ...payment,
      amountTotal: currencyFormatter(currency.type, {
        maximumFractionDigits: 0,
      }).format(getPrice(payment.amountTotal, currency.precision)),
    })),
  };
};

const paymentsQuery = (params: Parameters<typeof getPayments>[0]) =>
  queryOptions({
    queryFn: ({ signal }) => getPayments(params, { signal }),
    queryKey: ["admin", "payments", params],
    select,
  });

export { path, Payments };
